import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Multi-currency display layer. Server-side prices are stored in CAD (the
 * base). When the customer flips the switcher, we fetch live rates from
 * /api/currency/rates and apply them on the fly to all displayed prices.
 *
 * Checkout/orders ALWAYS submit in CAD (base) — the switcher is purely
 * presentational, which avoids subtle bugs from stale rates at payment time.
 *
 *   const { currency, setCurrency, format, fmt } = useCurrency();
 *   {format(product.price)}            // "$4.99 CAD" or "$3.69 USD"
 *   {fmt(product.price)}                // "$4.99" no suffix
 */
const CurrencyContext = createContext(null);
const KEY = 'currency';
const SUPPORTED = ['CAD', 'USD', 'EUR', 'GBP'];

function getInitial() {
  if (typeof window === 'undefined') return 'CAD';
  const saved = localStorage.getItem(KEY);
  return SUPPORTED.includes(saved) ? saved : 'CAD';
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(getInitial);
  const [rates, setRates] = useState({ CAD: 1 });

  useEffect(() => {
    fetch('/api/currency/rates')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        // Expected shape: [{ currency_code, rate_to_cad }, …]
        const map = { CAD: 1 };
        for (const r of d) {
          if (r.currency_code && r.rate_to_cad) map[r.currency_code] = Number(r.rate_to_cad);
        }
        setRates(map);
      })
      .catch(() => {});
  }, []);

  const setCurrency = (code) => {
    if (!SUPPORTED.includes(code)) return;
    setCurrencyState(code);
    try { localStorage.setItem(KEY, code); } catch (_) {}
  };

  // Convert a price stored in CAD to the active currency
  const convert = (cad) => {
    if (!cad && cad !== 0) return cad;
    const r = rates[currency] || 1;
    // rate_to_cad means: 1 unit_of_currency = r CAD; so target = cad / r
    return Number(cad) / r;
  };

  const symbols = { CAD: '$', USD: '$', EUR: '€', GBP: '£' };
  const fmt = (cad) => {
    const v = convert(cad);
    return `${symbols[currency] || '$'}${Number(v).toFixed(2)}`;
  };
  const format = (cad) => `${fmt(cad)} ${currency}`;

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, convert, fmt, format, supported: SUPPORTED }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext) || {
    currency: 'CAD', setCurrency: () => {}, rates: { CAD: 1 },
    convert: v => v, fmt: v => `$${Number(v).toFixed(2)}`, format: v => `$${Number(v).toFixed(2)} CAD`,
    supported: ['CAD'],
  };
}

export default CurrencyProvider;
